import React, { useState, useCallback, useMemo, useRef } from 'react';
import { View, FlatList, TouchableOpacity, StyleSheet, Dimensions, Platform, ScrollView, Modal, Alert, ActivityIndicator } from 'react-native';
import { Text, YStack, XStack, H2, H4, Button, Input, TextArea } from 'tamagui';
import { Card } from '@constants/Themed';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialIcons, AntDesign, Feather } from '@expo/vector-icons';
import Editor from '@monaco-editor/react';
import * as Animatable from 'react-native-animatable';
import { useUtilities } from '../../../../contexts/UtilitiesContext';
import apis, { generateAIFunction, getUtilitiesForData, saveUtility } from '@api/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useGlobalUser from '@hooks/getGlobalUser';
import parseByDelimiter from '../../../../utils/parseByDelimiter';
import { useRoute } from '@react-navigation/native';

const UtilitiesScreen = () => {
  const { utilities, getAllCategories, getUtilityByCategory } = useUtilities();
  const [selectedUtility, setSelectedUtility] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [aiDialogVisible, setAIDialogVisible] = useState(false);
  const [aiFormData, setAIFormData] = useState({
    description: '',
    language: 'javascript',
    category: 'Utility Functions',
    requirements: '',
    dataId: null
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'text'>('code');
  const [editorContent, setEditorContent] = useState<string>('');
  const theme = themeSelector();
  const [screenData, setScreenData] = useState({ width: Dimensions.get('window').width, height: Dimensions.get('window').height });
  const [isEditorVisibleForMobile, setIsEditorVisibleForMobile] = useState(false);
  const selectedId = useGlobalUser();
  const queryClient = useQueryClient();
  const route = useRoute();
  const editorRef = useRef<any>(null);
  
  // Get dataId from route params if available
  const routeDataId = route.params?.id;



  const isLargeScreen = screenData.width > 850;
  const showEditor = isLargeScreen || isEditorVisibleForMobile;

  // Ensure we're using the full available width
  const containerWidth = screenData.width;

  const leftPanelWidth = useMemo(() => {
    if (isLargeScreen) {
      return Math.min(400, containerWidth * 0.4);
    }
    return showEditor ? 120 : containerWidth - 80;
  }, [isLargeScreen, showEditor, containerWidth]);

  const rightPanelWidth = useMemo(() => {
    if (!showEditor) return 0;
    return containerWidth - leftPanelWidth - 100;
  }, [showEditor, containerWidth, leftPanelWidth]);

  // Fetch user data folders
  const { status, data: dataFolderList, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['dataFolders', selectedId],
    queryFn: async () => {
      const data = await apis.getDataFolders({ userId: selectedId, dataFilter: "all" });
      if (!data?.length) {
        return [];
      }
      return data;
    },
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  const filteredDataFolders = useMemo(() => {
    if (!dataFolderList?.length) return [];
    if (selectedCategory === 'All') return dataFolderList;
    
    // Filter by data type/tags
    return dataFolderList.filter(item => 
      item.dataTags?.includes(selectedCategory.toLowerCase()) ||
      item.dataType?.toLowerCase().includes(selectedCategory.toLowerCase())
    );
  }, [selectedCategory, dataFolderList]);

  // Handle refresh
  const onRefresh = useCallback(() => {
    setRefreshing(true);
    refetch().finally(() => setRefreshing(false));
  }, [refetch]);

  // Fetch saved utilities for selected data folder
  const { data: savedUtilities, refetch: refetchUtilities } = useQuery({
    queryKey: ['utilities', selectedData?._id],
    queryFn: async () => {
      if (!selectedData?._id) return [];
      const response = await getUtilitiesForData(selectedData._id);
      return response.data || [];
    },
    enabled: !!selectedData?._id,
    retry: 2
  });

  // Auto-select data if route param provided
  React.useEffect(() => {
    if (routeDataId && dataFolderList?.length) {
      const dataItem = dataFolderList.find((item: any) => item._id === routeDataId);
      if (dataItem) {
        setSelectedData(dataItem);
      }
    }
  }, [routeDataId, dataFolderList]);

  // Handle data selection with usage tracking
  const handleDataSelection = useCallback(async (dataItem) => {
    // Track data usage
    try {
      await apis.trackDataView(dataItem._id);
    } catch (error) {
      console.warn('Failed to track data usage:', error);
      // Don't break user flow
    }
    
    setSelectedData(dataItem);
    setSelectedUtility(null); // Clear utility selection when selecting data
    setEditorMode('text'); // Default to text mode for new data selection
    setEditorContent(''); // Clear editor content
    if (!isLargeScreen) {
      setIsEditorVisibleForMobile(true);
    }
  }, [isLargeScreen]);


  // Parse schema fields from selected data
  const schemaFields = useMemo(() => {
    if (!selectedData?.dataSchema || !Array.isArray(selectedData.dataSchema)) {
      return [];
    }
    const extractFields = (items: any[]): string[] => {
      const fields: string[] = [];
      items.forEach((item: any) => {
        const fieldName = item.title || item.name || item.key;
        if (fieldName) {
          fields.push(fieldName);
        }
        // Handle nested fields
        if (item.fields && Array.isArray(item.fields)) {
          fields.push(...extractFields(item.fields));
        }
        if (item.children && Array.isArray(item.children)) {
          fields.push(...extractFields(item.children));
        }
      });
      return fields;
    };
    return extractFields(selectedData.dataSchema);
  }, [selectedData?.dataSchema]);

  // Get data categories dynamically from the data
  const dataCategories = useMemo(() => {
    if (!dataFolderList?.length) return ['All'];

    const types = new Set(['All']);
    dataFolderList.forEach(item => {
      if (item.dataType) types.add(item.dataType);
      if (item.dataTags) {
        item.dataTags.forEach(tag => types.add(tag));
      }
    });

    return Array.from(types);
  }, [dataFolderList]);
  // Handle Monaco editor mount
  const handleEditorMount = useCallback((editor: any) => {
    editorRef.current = editor;
  }, []);

  // Insert field into editor at cursor position
  const insertFieldIntoEditor = useCallback((fieldName: string) => {
    if (!editorRef.current) return;
    
    try {
      const selection = editorRef.current.getSelection();
      if (selection) {
        // Insert at selection
        editorRef.current.executeEdits('insert-field', [{
          range: selection,
          text: fieldName
        }]);
      } else {
        // Insert at cursor position
        const position = editorRef.current.getPosition();
        if (position) {
          // Create range using editor's model
          const model = editorRef.current.getModel();
          if (model) {
            const range = {
              startLineNumber: position.lineNumber,
              startColumn: position.column,
              endLineNumber: position.lineNumber,
              endColumn: position.column
            };
            editorRef.current.executeEdits('insert-field', [{
              range: range,
              text: fieldName
            }]);
          }
        }
      }
    } catch (error) {
      console.error('Error inserting field into editor:', error);
    }
  }, []);

  // Handle utility selection with usage tracking
  const handleUtilitySelection = useCallback(async (utility) => {
    // Track utility usage - since utilities are static, we'll track by utility name/id
    try {
      await apis.trackUsage(utility.id, 'selected', {
        utilityName: utility.name,
        category: utility.category,
        source: 'UtilitiesScreen'
      });
    } catch (error) {
      console.warn('Failed to track utility usage:', error);
      // Don't break user flow
    }
    
    setSelectedUtility(utility);
    setEditorMode('code');
    if (utility.code) {
      setEditorContent(utility.code);
    }
  }, []);

  // Handle AI function generation for data
  const handleGenerateDataFunction = useCallback(async () => {
    if (!selectedData) {
      Alert.alert('Error', 'Please select a data item first.');
      return;
    }

    // Use textContent from editor if in text mode, otherwise use description from form
    const textContent = editorMode === 'text' && editorContent.trim() 
      ? editorContent.trim() 
      : aiFormData.description.trim();

    if (!textContent) {
      Alert.alert('Error', 'Please provide a description for the function.');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await generateAIFunction({
        description: editorMode === 'text' ? undefined : aiFormData.description,
        textContent: editorMode === 'text' ? editorContent.trim() : undefined,
        language: aiFormData.language,
        category: aiFormData.category,
        requirements: aiFormData.requirements || undefined,
        dataId: selectedData._id,
        dataSchema: selectedData.dataSchema,
        dataType: selectedData.dataType,
        dataTags: selectedData.dataTags
      });

      if (response.success) {
        // Utility is now saved to database, fetch it
        if (response.data.saved && response.data.id) {
          // Invalidate and refetch utilities
          queryClient.invalidateQueries({ queryKey: ['utilities', selectedData._id] });
          const utilities = await refetchUtilities();
          
          // Find and select the newly created utility
          const utilitiesData = utilities?.data || savedUtilities || [];
          const newUtility = utilitiesData.find((u: any) => u._id === response.data.id);
          if (newUtility) {
            setSelectedUtility(newUtility);
          } else {
            // Create utility object from response if not found in list yet
            const utilityFromResponse = {
              _id: response.data.id,
              title: response.data.title,
              description: response.data.description,
              category: response.data.category,
              code: response.data.code,
              language: response.data.language,
              dependencies: response.data.dependencies,
              examples: response.data.examples,
              explanation: response.data.explanation,
              isAIGenerated: true,
              dataId: selectedData._id
            };
            setSelectedUtility(utilityFromResponse);
          }
        } else {
          // Fallback: create utility object from response (if not saved)
          const newUtility = {
            _id: response.data.id,
            title: response.data.title,
            description: response.data.description,
            category: response.data.category,
            code: response.data.code,
            language: response.data.language,
            dependencies: response.data.dependencies,
            examples: response.data.examples,
            explanation: response.data.explanation,
            isAIGenerated: true,
            dataId: selectedData._id
          };
          setSelectedUtility(newUtility);
        }

        if (!isLargeScreen) {
          setIsEditorVisibleForMobile(true);
        }
        setAIDialogVisible(false);
        setEditorMode('code'); // Switch to code mode to show generated code
        setAIFormData({
          description: '',
          language: 'javascript',
          category: 'Utility Functions',
          requirements: '',
          dataId: null
        });
        setEditorContent(''); // Clear text content

        Alert.alert('Success', `AI function generated successfully for ${selectedData.dataTitle}!`);
      } else {
        throw new Error(response.error || 'Failed to generate function');
      }
    } catch (error) {
      console.error('AI Generation Error:', error);
      Alert.alert('Error', error.message || 'Failed to generate AI function. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [aiFormData, selectedData, isLargeScreen, queryClient, savedUtilities, refetchUtilities, editorMode, editorContent]);

  // Handle AI function generation
  const handleGenerateAIFunction = useCallback(async () => {
    if (!aiFormData.description.trim()) {
      Alert.alert('Error', 'Please provide a description for the function.');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await generateAIFunction({
        description: aiFormData.description,
        language: aiFormData.language,
        category: aiFormData.category,
        requirements: aiFormData.requirements || undefined
      });

      if (response.success) {
        // Create a new utility object from the AI response
        const newUtility = {
          id: response.data.id,
          title: response.data.title,
          name: response.data.title,
          description: response.data.description,
          category: response.data.category,
          code: response.data.code,
          language: response.data.language,
          dependencies: response.data.dependencies,
          examples: response.data.examples,
          explanation: response.data.explanation,
          icon: 'auto-fix',
          isAIGenerated: true,
          createdAt: new Date().toISOString()
        };

        // Set the new utility as selected
        setSelectedUtility(newUtility);
        if (!isLargeScreen) {
          setIsEditorVisibleForMobile(true);
        }
        setAIDialogVisible(false);
        setAIFormData({
          description: '',
          language: 'javascript',
          category: 'Utility Functions',
          requirements: '',
          dataId: null
        });

        Alert.alert('Success', 'AI function generated successfully!');
      } else {
        throw new Error(response.error || 'Failed to generate function');
      }
    } catch (error) {
      console.error('AI Generation Error:', error);
      Alert.alert('Error', error.message || 'Failed to generate AI function. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [aiFormData, isLargeScreen]);

  const renderDataItem = ({ item, index }) => {
    const isSelected = selectedData?._id === item._id;
    const isCompact = !isLargeScreen && showEditor;
    
    return (
      <View
        style={isCompact ? { flex: 1, margin: 4 } : {}}
      >
        <TouchableOpacity
          style={[
            styles.utilityItem,
            isCompact && styles.utilityItemCompact,
            {
              backgroundColor: isSelected 
                ? theme ? "#0ea5e9" : "#38bdf8"
                : theme ? "#ffffff" : "#334155",
              borderColor: isSelected 
                ? theme ? "#0ea5e9" : "#38bdf8"
                : theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)",
            }
          ]}
          onPress={() => handleDataSelection(item)}
        >
          <View style={[styles.utilityHeader, isCompact && styles.utilityHeaderCompact]}>
            <View style={[
              styles.iconContainer,
              {
                backgroundColor: isSelected 
                  ? "rgba(255,255,255,0.2)" 
                  : theme ? "#f0f9ff" : "#0f172a"
              },
              isCompact && styles.iconContainerCompact
            ]}>
              <MaterialIcons
                name="folder"
                size={isCompact ? 24 : 20}
                color={isSelected ? "#ffffff" : (theme ? "#64748b" : "#94a3b8")}
              />
            </View>
            <View style={[styles.utilityInfo, isCompact && styles.utilityInfoCompact]}>
              <Text style={[
                styles.utilityTitle,
                { color: isSelected ? "#ffffff" : (theme ? "#1e293b" : "#f1f5f9") },
                isCompact && styles.utilityTitleCompact
              ]}>
                {parseByDelimiter(item.dataTitle)}
              </Text>
              {!isCompact && (
                <Text style={[
                  styles.utilityCategory,
                  { color: isSelected ? "rgba(255,255,250.8)" : (theme ? "#64748b" : "#94a3b8") }
                ]}>
                  {item.dataType || 'General Data'} • {item.count || 0} items
                </Text>
              )}
            </View>
          </View>
          {!isCompact && (
            <>
              <Text style={[
                styles.utilityDescription,
                { color: isSelected ? "rgba(255,255,255,0.9)" : (theme ? "#64748b" : "#94a3b8") }
              ]}>
                {item.description || `Data collection with ${item.dataTags?.join(', ') || 'various tags'}`}
              </Text>
              {item.lastViewedAt && (
                <Text style={[
                  styles.utilityCategory,
                  {
                    color: isSelected ? "rgba(255,255,255,0.7)" : (theme ? "#94a3b8" : "#64748b"),
                    marginTop: 8,
                    fontSize: 12
                  }
                ]}>
                  Last viewed: {new Date(item.lastViewedAt).toLocaleDateString()}
                </Text>
              )}
            </>
          )}
        </TouchableOpacity>
      </View>
    );
  };

  const renderCategoryButton = (category) => (
    <TouchableOpacity
      key={category}
      style={[
        styles.categoryButton,
        {
          backgroundColor: selectedCategory === category 
            ? theme ? "#0ea5e9" : "#38bdf8"
            : theme ? "#f8fafc" : "#334155",
        }
      ]}
      onPress={() => setSelectedCategory(category)}
    >
      <Text style={[
        styles.categoryButtonText,
        {
          color: selectedCategory === category 
            ? "#ffffff" 
            : theme ? "#64748b" : "#94a3b8"
        }
      ]}>
        {category}
      </Text>
    </TouchableOpacity>
  );

  // Render schema field item
  const renderSchemaField = useCallback((fieldName: string, index: number) => (
    <TouchableOpacity
      key={`${fieldName}-${index}`}
      style={[
        styles.schemaFieldItem,
        {
          backgroundColor: theme ? "#f8fafc" : "#0f172a",
          borderColor: theme ? "#e2e8f0" : "#334155"
        }
      ]}
      onPress={() => insertFieldIntoEditor(fieldName)}
    >
      <MaterialIcons
        name="label"
        size={16}
        color={theme ? "#64748b" : "#94a3b8"}
        style={{ marginRight: 8 }}
      />
      <Text style={[
        styles.schemaFieldText,
        { color: theme ? "#1e293b" : "#f1f5f9" }
      ]}>
        {fieldName}
      </Text>
    </TouchableOpacity>
  ), [theme, insertFieldIntoEditor]);

  return (
    <DefaultScreen layoutCallback={(layout) => {
      setScreenData(layout)}} style={{ flex: 1, gap: 24, width: '100%' }} containerStyle={{ flex: 1, width: '100%' }}>
      {/* Header Section */}
      <View
        style={[
          styles.header,
          {
            backgroundColor: theme ? "#ffffff" : "#1e293b",
            borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
          }
        ]}
      >
        <View style={styles.headerContent}>
          <View style={[
            styles.headerIcon,
            { backgroundColor: theme ? "#f0f9ff" : "#0f172a" }
          ]}>
            <MaterialIcons
              name="folder-special"
              size={28}
              color={theme ? "#0ea5e9" : "#38bdf8"}
            />
          </View>
          <View style={{ flex: 1 }}>
            <H4 style={[
              styles.headerTitle,
              { color: theme ? "#1e293b" : "#f1f5f9" }
            ]}>
              Data Utilities Generator
            </H4>
            <Text style={[
              styles.headerSubtitle,
              { color: theme ? "#64748b" : "#94a3b8" }
            ]}>
              Generate AI-powered utilities for your data schemas
            </Text>
          </View>
          <TouchableOpacity
            style={[
              styles.aiButton,
              { 
                backgroundColor: theme ? "#8b5cf6" : "#7c3aed",
                opacity: selectedData ? 1 : 0.6
              }
            ]}
            onPress={() => {
              if (selectedData) {
                setAIFormData(prev => ({ ...prev, dataId: selectedData._id }));
                setAIDialogVisible(true);
              } else {
                Alert.alert('Select Data', 'Please select a data item first to generate utilities for it.');
              }
            }}
            disabled={!selectedData}
          >
            <AntDesign name="plus" size={16} color="#ffffff" />
            <Text style={styles.aiButtonText}>Generate AI Function</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Category Filter */}
      <View
        style={styles.categoryContainer}
      >
        <View style={styles.categoryRow}>
          {dataCategories.map(renderCategoryButton)}
        </View>
      </View>

      {/* Main Content Area */}
      <XStack flex={1} gap={24} alignItems="stretch" width="100%" alignSelf="stretch">
        {/* Left Panel - Utilities List */}
        <Animatable.View
          animation="fadeIn"
          duration={300}
          style={[
            styles.leftPanel,
            {
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
              width: leftPanelWidth
            }
          ]}
        >
          <View style={[
            styles.panelHeader,
            { borderBottomColor: theme ? "#e2e8f0" : "#334155" }
          ]}>
            <H4 style={[
              styles.panelTitle,
              { color: theme ? "#1e293b" : "#f1f5f9" }
            ]}>
              {selectedData ? 'Saved Utilities' : 'Data Collections'}
            </H4>
            <Text style={[
              styles.panelCount,
              { color: theme ? "#64748b" : "#94a3b8" }
            ]}>
              {selectedData 
                ? `${savedUtilities?.length || 0} utilities`
                : `${filteredDataFolders.length} items`
              }
            </Text>
          </View>
          
          {selectedData ? (
            <FlatList
              data={savedUtilities || []}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.utilityItem,
                    {
                      backgroundColor: selectedUtility?._id === item._id
                        ? (theme ? "#e0f2fe" : "#1e3a5f")
                        : (theme ? "#f8fafc" : "#0f172a"),
                      borderColor: selectedUtility?._id === item._id
                        ? "#0ea5e9"
                        : (theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)")
                    }
                  ]}
                  onPress={() => setSelectedUtility(item)}
                >
                  <View style={styles.utilityHeader}>
                    <MaterialIcons
                      name="code"
                      size={20}
                      color={selectedUtility?._id === item._id ? "#0ea5e9" : (theme ? "#64748b" : "#94a3b8")}
                    />
                    <View style={styles.utilityInfo}>
                      <Text style={[
                        styles.utilityTitle,
                        { color: selectedUtility?._id === item._id ? "#0ea5e9" : (theme ? "#1e293b" : "#f1f5f9") }
                      ]}>
                        {item.title}
                      </Text>
                      <Text style={[
                        styles.utilityCategory,
                        { color: theme ? "#64748b" : "#94a3b8" }
                      ]}>
                        {item.language || 'javascript'} • {item.isActive ? 'Active' : 'Inactive'}
                      </Text>
                    </View>
                  </View>
                </TouchableOpacity>
              )}
              keyExtractor={(item) => item._id}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.listContainer}
              ListEmptyComponent={() => (
                <View style={{ padding: 20, alignItems: 'center' }}>
                  <Text style={{ color: theme ? "#64748b" : "#94a3b8", textAlign: 'center' }}>
                    No utilities found for this data folder.{'\n'}Generate one using the AI button above.
                  </Text>
                </View>
              )}
            />
          ) : (
            <FlatList
              data={filteredDataFolders}
              renderItem={renderDataItem}
              keyExtractor={(item) => item._id || item.id}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.listContainer}
              refreshing={refreshing}
              onRefresh={onRefresh}
              ListEmptyComponent={() => (
                <View style={{ padding: 20, alignItems: 'center' }}>
                  <Text style={{ color: theme ? "#64748b" : "#94a3b8" }}>
                    {isLoading ? 'Loading data...' : 'No data folders found'}
                  </Text>
                </View>
              )}
            />
          )}
        </Animatable.View>

        {/* Right Panel - Code Editor */}
        {showEditor && (
          <Animatable.View
            animation="fadeInRight"
            duration={300}
            style={[
              styles.rightPanel,
              {
                backgroundColor: theme ? "#ffffff" : "#1e293b",
                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
                width: rightPanelWidth
              }
            ]}
          >
            <View style={[
              styles.panelHeader,
              { borderBottomColor: theme ? "#e2e8f0" : "#334155" }
            ]}>
              <View style={styles.codeHeader}>
                <View style={[
                  styles.codeIcon,
                  { backgroundColor: theme ? "#f0fdf4" : "#0f172a" }
                ]}>
                  <Feather
                    name={selectedData ? "database" : "code"}
                    size={20}
                    color={theme ? "#059669" : "#10b981"}
                  />
                </View>
                <View>
                  <H4 style={[
                    styles.panelTitle,
                    { color: theme ? "#1e293b" : "#f1f5f9" }
                  ]}>
                    {selectedData ? parseByDelimiter(selectedData.dataTitle) : (selectedUtility?.title || 'Select Data')}
                  </H4>
                  <Text style={[
                    styles.codeLanguage,
                    { color: theme ? "#64748b" : "#94a3b8" }
                  ]}>
                    {selectedData
                      ? `${selectedData.dataType || 'Data'} • ${selectedData.count || 0} items`
                      : (selectedUtility?.language?.toUpperCase() + ' • Ready to copy' || 'No selection')
                    }
                  </Text>
                </View>
              </View>

              {!isLargeScreen && (
                <TouchableOpacity onPress={() => setIsEditorVisibleForMobile(false)} style={{ padding: 8 }}>
                  <AntDesign name="close" size={20} color={theme ? "#64748b" : "#94a3b8"} />
                </TouchableOpacity>
              )}
            </View>

            {/* Tab Toggle */}
            {selectedData && (
              <View style={[
                styles.tabContainer,
                { borderBottomColor: theme ? "#e2e8f0" : "#334155" }
              ]}>
                <TouchableOpacity
                  style={[
                    styles.tabButton,
                    {
                      backgroundColor: editorMode === 'code'
                        ? (theme ? "#e0f2fe" : "#1e3a5f")
                        : 'transparent',
                      borderBottomColor: editorMode === 'code'
                        ? "#0ea5e9"
                        : 'transparent'
                    }
                  ]}
                  onPress={() => setEditorMode('code')}
                >
                  <MaterialIcons
                    name="code"
                    size={16}
                    color={editorMode === 'code' ? "#0ea5e9" : (theme ? "#64748b" : "#94a3b8")}
                    style={{ marginRight: 6 }}
                  />
                  <Text style={[
                    styles.tabButtonText,
                    {
                      color: editorMode === 'code'
                        ? "#0ea5e9"
                        : (theme ? "#64748b" : "#94a3b8")
                    }
                  ]}>
                    Code
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.tabButton,
                    {
                      backgroundColor: editorMode === 'text'
                        ? (theme ? "#e0f2fe" : "#1e3a5f")
                        : 'transparent',
                      borderBottomColor: editorMode === 'text'
                        ? "#0ea5e9"
                        : 'transparent'
                    }
                  ]}
                  onPress={() => setEditorMode('text')}
                >
                  <MaterialIcons
                    name="text-fields"
                    size={16}
                    color={editorMode === 'text' ? "#0ea5e9" : (theme ? "#64748b" : "#94a3b8")}
                    style={{ marginRight: 6 }}
                  />
                  <Text style={[
                    styles.tabButtonText,
                    {
                      color: editorMode === 'text'
                        ? "#0ea5e9"
                        : (theme ? "#64748b" : "#94a3b8")
                    }
                  ]}>
                    Text
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Schema Fields Display */}
            {selectedData && schemaFields.length > 0 && (
              <View style={[
                styles.schemaFieldsContainer,
                {
                  backgroundColor: theme ? "#f8fafc" : "#0f172a",
                  borderBottomColor: theme ? "#e2e8f0" : "#334155"
                }
              ]}>
                <Text style={[
                  styles.schemaFieldsTitle,
                  { color: theme ? "#64748b" : "#94a3b8" }
                ]}>
                  Schema Fields (click to insert)
                </Text>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  style={styles.schemaFieldsScroll}
                  contentContainerStyle={styles.schemaFieldsContent}
                >
                  {schemaFields.map((fieldName, index) => renderSchemaField(fieldName, index))}
                </ScrollView>
              </View>
            )}

            <View style={styles.editorContainer}>
              <Editor
                height="100%"
                language={
                  selectedUtility
                    ? (selectedUtility.language || 'javascript')
                    : editorMode === 'code'
                      ? 'javascript'
                      : 'plaintext'
                }
                value={
                  selectedUtility
                    ? selectedUtility.code
                    : editorContent || (selectedData
                      ? (editorMode === 'text'
                        ? '// Write your utility description in natural language here...\n// Click on schema fields above to insert them into your description.'
                        : '// Select "Generate Function" to create AI-powered utilities for this data')
                      : '// Select a data item to view its schema or generate utilities')
                }
                theme={theme ? 'light' : 'vs-dark'}
                onMount={handleEditorMount}
                onChange={(value) => {
                  if (!selectedUtility) {
                    setEditorContent(value || '');
                  }
                }}
                options={{
                  readOnly: !!selectedUtility,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  fontSize: 14,
                  lineNumbers: 'on',
                  folding: true,
                  selectOnLineNumbers: true,
                  automaticLayout: true
                }}
              />
            </View>
          </Animatable.View>
        )}
      </XStack>

      {/* AI Function Generation Dialog */}
      <Modal
        visible={aiDialogVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setAIDialogVisible(false)}
      >
        <View style={styles.aiDialog}>
          <View style={[
            styles.aiDialogContent,
            { backgroundColor: theme ? "#ffffff" : "#1e293b" }
          ]}>
            <View style={styles.aiDialogHeader}>
              <Text style={[
                styles.aiDialogTitle,
                { color: theme ? "#1e293b" : "#f1f5f9" }
              ]}>
                Generate AI Function{selectedData ? ` for ${selectedData.dataTitle}` : ''}
              </Text>
              <TouchableOpacity
                style={[
                  styles.closeButton,
                  { backgroundColor: theme ? "#f1f5f9" : "#334155" }
                ]}
                onPress={() => setAIDialogVisible(false)}
              >
                <AntDesign name="close" size={16} color={theme ? "#64748b" : "#94a3b8"} />
              </TouchableOpacity>
            </View>

            <ScrollView showsVerticalScrollIndicator={false}>
              {/* Selected Data Context */}
              {selectedData && (
                <View style={[styles.formGroup, { 
                  backgroundColor: theme ? "#f0f9ff" : "#0f172a",
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16
                }]}>
                  <Text style={[
                    styles.formLabel,
                    { color: theme ? "#0ea5e9" : "#38bdf8", marginBottom: 8 }
                  ]}>
                    🎯 Selected Data Context
                  </Text>
                  <Text style={[
                    { color: theme ? "#1e293b" : "#f1f5f9", fontWeight: '600', marginBottom: 4 }
                  ]}>
                    {selectedData.dataTitle}
                  </Text>
                  <Text style={[
                    { color: theme ? "#64748b" : "#94a3b8", fontSize: 12, marginBottom: 8 }
                  ]}>
                    Type: {selectedData.dataType || 'General'} • Items: {selectedData.count || 0}
                  </Text>
                  {selectedData.dataTags?.length > 0 && (
                    <Text style={[
                      { color: theme ? "#64748b" : "#94a3b8", fontSize: 12 }
                    ]}>
                      Tags: {selectedData.dataTags.join(', ')}
                    </Text>
                  )}
                </View>
              )}

              <View style={styles.formGroup}>
                <Text style={[
                  styles.formLabel,
                  { color: theme ? "#1e293b" : "#f1f5f9" }
                ]}>
                  Function Description *
                </Text>
                <TextArea
                  style={[
                    styles.formTextArea,
                    {
                      backgroundColor: theme ? "#f8fafc" : "#334155",
                      borderColor: theme ? "#e2e8f0" : "#475569",
                      color: theme ? "#1e293b" : "#f1f5f9"
                    }
                  ]}
                  placeholder="Describe what you want the function to do..."
                  placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                  value={aiFormData.description}
                  onChangeText={(text) => setAIFormData(prev => ({ ...prev, description: text }))}
                  multiline
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={[
                  styles.formLabel,
                  { color: theme ? "#1e293b" : "#f1f5f9" }
                ]}>
                  Programming Language
                </Text>
                <TouchableOpacity
                  style={[
                    styles.formPicker,
                    {
                      backgroundColor: theme ? "#f8fafc" : "#334155",
                      borderColor: theme ? "#e2e8f0" : "#475569"
                    }
                  ]}
                >
                  <Text style={{ color: theme ? "#1e293b" : "#f1f5f9" }}>
                    {aiFormData.language.charAt(0).toUpperCase() + aiFormData.language.slice(1)}
                  </Text>
                </TouchableOpacity>
              </View>

              <View style={styles.formGroup}>
                <Text style={[
                  styles.formLabel,
                  { color: theme ? "#1e293b" : "#f1f5f9" }
                ]}>
                  Category
                </Text>
                <Input
                  style={[
                    styles.formInput,
                    {
                      backgroundColor: theme ? "#f8fafc" : "#334155",
                      borderColor: theme ? "#e2e8f0" : "#475569",
                      color: theme ? "#1e293b" : "#f1f5f9"
                    }
                  ]}
                  placeholder="e.g., Data Processing, API Utils, etc."
                  placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                  value={aiFormData.category}
                  onChangeText={(text) => setAIFormData(prev => ({ ...prev, category: text }))}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={[
                  styles.formLabel,
                  { color: theme ? "#1e293b" : "#f1f5f9" }
                ]}>
                  Additional Requirements (Optional)
                </Text>
                <TextArea
                  style={[
                    styles.formTextArea,
                    {
                      backgroundColor: theme ? "#f8fafc" : "#334155",
                      borderColor: theme ? "#e2e8f0" : "#475569",
                      color: theme ? "#1e293b" : "#f1f5f9"
                    }
                  ]}
                  placeholder="Any specific requirements, libraries, or constraints..."
                  placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                  value={aiFormData.requirements}
                  onChangeText={(text) => setAIFormData(prev => ({ ...prev, requirements: text }))}
                  multiline
                />
              </View>

              <TouchableOpacity
                style={[
                  styles.generateButton,
                  isGenerating && styles.generateButtonDisabled
                ]}
                onPress={handleGenerateDataFunction}
                disabled={isGenerating || !aiFormData.description.trim()}
              >
                {isGenerating ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                    <AntDesign name="rocket" size={16} color="#ffffff" />
                )}
                <Text style={styles.generateButtonText}>
                  {isGenerating ? 'Generating...' : 'Generate Function'}
                </Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </DefaultScreen>
  );
};

const styles = StyleSheet.create({
  header: {
    borderRadius: 20,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 10,
    borderWidth: 1,
    width: '100%',
    alignSelf: 'stretch',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerIcon: {
    borderRadius: 12,
    padding: 12,
    marginRight: 16,
  },
  headerTitle: {
    fontWeight: '700',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
  },
  categoryContainer: {
    paddingHorizontal: 4,
    width: '100%',
    alignSelf: 'stretch',
  },
  categoryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    width: '100%',
    justifyContent: 'center',
  },
  categoryButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  categoryButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  leftPanel: {
    minWidth: 320,
    borderRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 10,
    borderWidth: 1,
    overflow: 'hidden',
  },
  rightPanel: {
    borderRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.08,
    shadowRadius: 24,
    elevation: 12,
    borderWidth: 1,
    overflow: 'hidden',
  },
  panelHeader: {
    padding: 20,
    borderBottomWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  panelTitle: {
    fontWeight: '700',
  },
  panelCount: {
    fontSize: 14,
  },
  codeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  codeIcon: {
    borderRadius: 8,
    padding: 8,
    marginRight: 12,
  },
  codeLanguage: {
    fontSize: 12,
    marginTop: 2,
  },
  listContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  utilityItem: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    borderWidth: 1,
  },
  utilityItemCompact: {
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  utilityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  utilityHeaderCompact: {
    flexDirection: 'column',
    marginBottom: 0,
    gap: 8,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  iconContainerCompact: {
    marginRight: 0,
  },
  utilityInfo: {
    flex: 1,
  },
  utilityInfoCompact: {
    flex: 0,
    alignItems: 'center',
  },
  utilityTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
    flexShrink: 1,
  },
  utilityTitleCompact: {
    fontSize: 12,
    textAlign: 'center',
    fontWeight: '500',
    marginBottom: 0,
  },
  utilityCategory: {
    fontSize: 12,
    fontWeight: '500',
  },
  utilityDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  editorContainer: {
    flex: 1,
    padding: 20,
  },
  aiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    gap: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  aiButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  aiDialog: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  aiDialogContent: {
    width: '100%',
    maxWidth: 500,
    borderRadius: 16,
    padding: 24,
    maxHeight: '80%',
  },
  aiDialogHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  aiDialogTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  closeButton: {
    padding: 8,
    borderRadius: 8,
  },
  formGroup: {
    marginBottom: 20,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  formInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 44,
  },
  formTextArea: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 100,
    textAlignVertical: 'top',
  },
  formPicker: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 44,
  },
  generateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#8b5cf6',
    padding: 14,
    borderRadius: 8,
    gap: 8,
    marginTop: 10,
  },
  generateButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  generateButtonDisabled: {
    backgroundColor: '#94a3b8',
  },
  tabContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    paddingHorizontal: 20,
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 2,
    marginRight: 8,
  },
  tabButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  schemaFieldsContainer: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  schemaFieldsTitle: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  schemaFieldsScroll: {
    maxHeight: 60,
  },
  schemaFieldsContent: {
    paddingRight: 20,
  },
  schemaFieldItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    marginRight: 8,
  },
  schemaFieldText: {
    fontSize: 13,
    fontWeight: '500',
  },
});

export default UtilitiesScreen;
